import { useEffect, useMemo, useRef, useState } from "react";
import { AutoComplete, Avatar, Tag, Row, Col, Spin, Empty, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { search, topic } from "../../store";
import "./searchBar.css";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 1;

// Fallback images per type
const FALLBACK_IMAGES = {
  TOPIC: "/default-topic.svg",
  CATEGORY: "/default-category.svg",
  COACH: "/default-avatar.png",
};

const TAG_COLORS = {
  TOPIC: { bg: "rgba(59, 130, 246, 0.1)", color: "#2563EB" },
  CATEGORY: { bg: "rgba(16, 185, 129, 0.1)", color: "#059669" },
  COACH: { bg: "rgba(139, 92, 246, 0.1)", color: "#7C3AED" },
};

const resolvePictureSrc = (picture) => {
  if (!picture) return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  const apiUri = process.env.REACT_APP_API_URI || "";
  const serverRoot = apiUri.replace(/\/api\/?$/, "");
  return `${serverRoot}${picture.startsWith("/") ? picture : `/${picture}`}`;
};

const SearchBar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { results, isLoading } = useSelector((state) => state.search);
  const [query, setQuery] = useState("");
  const [failedImageKeys, setFailedImageKeys] = useState(() => new Set());
  const debounceRef = useRef(null);

  useEffect(() => {
    setFailedImageKeys(new Set());
  }, [results]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      dispatch(search.reset());
      return;
    }

    debounceRef.current = setTimeout(() => {
      dispatch(search.searchApi({ q: trimmed }));
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, dispatch]);

  // Build AutoComplete options from search results
  const options = useMemo(() => {
    return results.map((item) => {
      const imageKey = `${item.type}:${item.id}`;
      const imgSrc = failedImageKeys.has(imageKey)
        ? FALLBACK_IMAGES[item.type]
        : resolvePictureSrc(item.picture) || FALLBACK_IMAGES[item.type];
      const tagStyle = TAG_COLORS[item.type] || TAG_COLORS.TOPIC;

      const label = (
        <div className="search-suggestion-item" key={item.id}>
          <Avatar
            className="search-suggestion-avatar"
            size={40}
            shape="square"
            src={imgSrc}
            onError={() => {
              setFailedImageKeys((prev) => {
                if (prev.has(imageKey)) return prev;
                const next = new Set(prev);
                next.add(imageKey);
                return next;
              });
            }}
          />
          <span className="search-suggestion-name">{item.name}</span>
          <Tag
            className="search-suggestion-tag"
            style={{
              backgroundColor: tagStyle.bg,
              color: tagStyle.color,
              border: "none",
              marginRight: 0,
            }}
          >
            {item.type === "CATEGORY"
              ? "Category"
              : item.type === "COACH"
                ? "Coach"
                : "Topic"}
          </Tag>
        </div>
      );

      return {
        value: `${item.type}:${item.id}`,
        label,
        item,
      };
    });
  }, [results, failedImageKeys]);

  const handleSelect = (_value, option) => {
    const item = option?.item;
    if (!item) return;

    setQuery("");

    if (item.type === "TOPIC") {
      navigate(`/topic/${item.id}`);
    } else if (item.type === "CATEGORY") {
      // Navigate to Explore and select the category by dispatching topic fetch
      dispatch(topic.resetPagination());
      dispatch(topic.topicApi({ categoryId: item.id, limit: 10, skip: 0 }));
      navigate("/explore");
    } else if (item.type === "COACH") {
      navigate(`/coach/${item.id}`);
    }
  };

  // Enter key does nothing — only selection from suggestions works
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <Row justify="center">
      <Col xs={22} sm={18} md={12} lg={8} xl={6}>
        <AutoComplete
          className="searchbar"
          value={query}
          options={options}
          onSelect={handleSelect}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          notFoundContent={
            isLoading ? (
              <div className="search-loading">
                <Spin size="small" />
              </div>
            ) : query.trim().length >= MIN_QUERY_LENGTH ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No results found"
              />
            ) : null
          }
          popupClassName="search-dropdown"
        >
          <Input
            className="searchbar-input"
            prefix={<SearchOutlined className="searchbar-prefix-icon" />}
            placeholder="Search Coach, Category or Topic"
            allowClear
            aria-label="Search coaches, categories or topics"
          />
        </AutoComplete>
      </Col>
    </Row>
  );
};

export default SearchBar;
